import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * SubscriptionContext (M5 default-deny · 2026-07-10)
 *
 * The canonical entitlement snapshot returned by
 * `GET /api/subscription/entitlements` is the ONLY source used to
 * drive frontend visibility. Fallbacks that used to return `true` for
 * "no entitlement list found" have been removed — the frontend now
 * mirrors the backend: unknown module / feature / workflow = **denied**.
 *
 *   sub.loading        — first fetch in flight (UIs show a loader)
 *   sub.entitlements   — full payload
 *   sub.hasModule(k)   — canonical module gate (default deny)
 *   sub.hasFeature(k)  — canonical feature gate (default deny)
 *   sub.hasWorkflow(k) — canonical workflow gate (default deny)
 *   sub.canBilling     — RBAC for billing UI
 *   sub.refresh()      — manual refresh (called on window focus + plan events)
 *   sub.entitlementVersion — monotonically increases; use as React key when
 *                            you need to force-remount on plan change.
 */
const SubCtx = createContext(null);

const BILLING_PERMS = new Set([
  "subscription.select_plan",
  "subscription.checkout",
  "subscription.change_plan",
  "subscription.cancel",
  "subscription.manage_payment_method",
  "subscription.manage",
]);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(null); // null = loading
  const [error, setError] = useState("");
  const lastVersion = useRef(0);

  const fetchEntitlements = useCallback(async () => {
    if (!user || user === false) {
      setData(null);
      return;
    }
    try {
      const { data: resp } = await api.get("/subscription/entitlements");
      setData(resp?.data || null);
      setError("");
      const v = resp?.data?.entitlement_version;
      if (typeof v === "number") lastVersion.current = v;
    } catch (e) {
      setError(e?.message || "Failed to load subscription");
      // Fail closed — no entitlement means no access.
      setData({
        status: "NONE",
        features: {},
        modules: {},
        limits: {},
        workflow_entitlements: [],
        plan: null,
        subscription: null,
        canonical: null,
        is_full_access: false,
        is_grace: false,
        is_blocked: true,
        needs_plan_selection: true,
        days_remaining: null,
        entitlement_version: 0,
      });
    }
  }, [user]);

  useEffect(() => { fetchEntitlements(); }, [fetchEntitlements]);

  // Refresh on window focus + on custom events fired by plan-change flows.
  useEffect(() => {
    const onFocus = () => { fetchEntitlements(); };
    const onPlanEvent = () => { fetchEntitlements(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("certopz:plan-changed", onPlanEvent);
    window.addEventListener("certopz:entitlements-refresh", onPlanEvent);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("certopz:plan-changed", onPlanEvent);
      window.removeEventListener("certopz:entitlements-refresh", onPlanEvent);
    };
  }, [fetchEntitlements]);

  // ---- canonical (default-deny) accessors ------------------------------
  const hasModule = useCallback(
    (key) => {
      if (!data) return false; // loading → block (UI must show loader)
      const mods = data.modules;
      if (mods && typeof mods === "object") return Boolean(mods[key]);
      return false;
    },
    [data],
  );
  const hasFeature = useCallback(
    (key) => {
      if (!data) return false;
      const feats = data.features;
      if (feats && typeof feats === "object") return Boolean(feats[key]);
      return false;
    },
    [data],
  );
  const hasWorkflow = useCallback(
    (key) => {
      if (!data) return false;
      const list = data.workflow_entitlements;
      return Array.isArray(list) && list.includes(key);
    },
    [data],
  );
  const feature = hasFeature; // alias retained for existing callers
  const pricingLimits = data?.canonical?.limits || data?.pricing_limits || {};

  const canBilling = (() => {
    if (!user) return false;
    if ((user.roles || []).includes("Owner") || (user.roles || []).includes("Admin")) return true;
    const perms = new Set(user.permissions || []);
    for (const p of BILLING_PERMS) if (perms.has(p)) return true;
    return false;
  })();

  const value = {
    loading: data === null,
    entitlements: data,
    canonical: data?.canonical || null,
    status: data?.status || "NONE",
    plan: data?.plan || null,
    subscription: data?.subscription || null,
    daysRemaining: data?.days_remaining ?? null,
    isFullAccess: !!data?.is_full_access,
    isGrace: !!data?.is_grace,
    isBlocked: !!data?.is_blocked,
    needsPlanSelection: !!data?.needs_plan_selection,
    entitlementVersion: data?.entitlement_version ?? 0,
    planCombinationId: data?.plan_combination_id || null,
    feature,
    hasFeature,
    hasModule,
    hasWorkflow,
    pricingLimits,
    canBilling,
    error,
    refresh: fetchEntitlements,
  };

  return <SubCtx.Provider value={value}>{children}</SubCtx.Provider>;
}

export const useSubscription = () => useContext(SubCtx);
